import {observer} from 'mobx-react';
import React, {useContext, useEffect, useState, useMemo, useCallback, useRef} from 'react';
import {NetworkContext} from '../lib/context';
import {isAbortError} from '../../../lib/error-status';
import './roster-comparison-table.scss';
import SpecialtyMultiSelect from './specialty-multiselect';
import {
    Table,
    TableHead,
    TableRow,
    TableHeader,
    TableBody,
    TableCell,
    DataTable,
    DataTableSkeleton,
    Pagination,
    SkeletonText,
} from '@carbon/react';
import {CheckmarkFilled, CloseFilled} from '@carbon/icons-react';
import getFormatter from '../../../lib/formatter';
import {ParsedApiError} from '../../../lib/error-handler';
import handleError from '../../../lib/error';
import RosterBadge from '../../../components/roster-badge';
import ComparisonEmptyState from './comparison-empty-state';
import {useScreenReaderAnnouncement} from './comparison-empty-state/useScreenReaderAnnouncement';
import {formatRosterLabel} from '../lib/roster-label';
import {useProviderRosterList} from './hooks/use-provider-roster-list';
import {parseCountyValue, type AdequacyStandard, type ComparisonMeasure} from '../lib/stores/roster-comparison-store';
import {hasRosterData, MEASURE_STANDARDS, meetsMeasure, populationMetKey, rosterHasData} from '../lib/comparison-row';

const PAGE_SIZE = 15;

const METRIC_OPTIONS: {key: ComparisonMeasure; label: string; icon: React.ReactNode}[] = [
    {
        key: 'combined',
        label: 'Combined',
        icon: <><circle cx='6.2' cy='8' r='4' /><circle cx='9.8' cy='8' r='4' /></>,
    },
    {
        key: 'time',
        label: 'Time',
        icon: <><circle cx='8' cy='8' r='6.2' /><path d='M8 4.2V8l2.6 1.6' /></>,
    },
    {
        key: 'distance',
        label: 'Distance',
        icon: <>
            <path d='M2.2 10.6h11.6M3.4 10.6V8l1.4-3h6.4l1.4 3v2.6' />
            <circle cx='5.1' cy='11.9' r='1.1' /><circle cx='10.9' cy='11.9' r='1.1' />
        </>,
    },
];

// Moves between options with the arrow keys, selecting as it goes (radio group behaviour)
const METRIC_KEY_OFFSETS: Record<string, number> = {ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1};

// Mirrors the loaded layout (county header card, then the table) so every loading state matches it
const ComparisonTableSkeleton = ({headers}: Pick<React.ComponentProps<typeof DataTableSkeleton>, 'headers'>) => (
    <div className='bg-white' style={{border: '1px solid #e0e0e0', borderRadius: '4px', overflow: 'hidden'}}>
        <div className='p-4'>
            <div className='flex flex-wrap items-start justify-between gap-4'>
                <div className='flex-1'>
                    <SkeletonText heading width='40%' />
                    <SkeletonText paragraph lineCount={2} width='70%' />
                </div>
                <div className='flex-shrink-0' style={{width: '18rem', maxWidth: '100%'}}>
                    <SkeletonText width='100%' />
                </div>
            </div>
        </div>
        <DataTableSkeleton
            headers={headers}
            showHeader={false}
            showToolbar={false}
            rowCount={9}
            columnCount={headers.length}
        />
    </div>
);

// The caller's label names the roster and states the outcome, so a cell reads "Roster 2: does not meet
// combined standard"; the check and cross differ in shape, so colour is never the only cue
const ComplianceIcon = React.memo(({isCompliant, ariaLabel}: {isCompliant: boolean; ariaLabel?: string}) => {
    return isCompliant ? (
        <CheckmarkFilled className='comparison-icon comparison-icon--pass' size={20} aria-label={ariaLabel || 'Compliant'} data-testid='checkmark-icon' />
    ) : (
        <CloseFilled className='comparison-icon comparison-icon--fail' size={20} aria-label={ariaLabel || 'Non-compliant'} data-testid='close-icon' />
    );
});

// What a tick means on each measure. Combined's percentages sit beside its tick rather than
// replacing it, so the tick still means both standards there
const MEETS_LABELS: Record<ComparisonMeasure, string> = {
    combined: 'Meets both the time and distance standards',
    time: 'Meets the time standard',
    distance: 'Meets the distance standard',
};

// Each view opens with its own percentages setting, per the design: Combined without, Time and
// Distance with. Switching view resets the toggle rather than carrying it over
const DEFAULT_PERCENTAGES: Record<ComparisonMeasure, boolean> = {
    combined: false,
    time: true,
    distance: true,
};

// The header's label for each figure beside a tick
const STANDARD_LABELS: Record<AdequacyStandard, string> = {
    time: 'Time',
    distance: 'Distance',
};

// Built once: a table page formats up to two figures per roster per row
const percentFloor = getFormatter('percentfloor', 0.1);

const formatPercent = (value: any): string => {
    if (value === null || value === undefined || value === '' || isNaN(Number(value))) return '—';
    return percentFloor.format(value);
};

type ComparisonMessageProps = {
    title: string;
    body: string;
    testId: string;
};

// A centred message in place of the comparison table, for any state that blocks it
const ComparisonMessage = ({title, body, testId}: ComparisonMessageProps) => (
    <div className='bg-white p-8 rounded-lg shadow-sm flex items-center justify-center h-64 mt-8' data-testid={testId}>
        <div className='flex flex-col items-center gap-3 text-center'>
            <h4 className='m-0 text-base font-semibold text-[#161616]'>{title}</h4>
            <p className='m-0 max-w-md text-sm text-[#525252]'>{body}</p>
        </div>
    </div>
);

// Messages for roster-list states that leave nothing to compare. Checked before the user's own
// selections: without a roster list, no selection can be made.
const ROSTER_LIST_MESSAGES: Record<'empty' | 'error', ComparisonMessageProps> = {
    empty: {
        title: 'No rosters available',
        body: 'No rosters have been uploaded for this client yet. Add one in Roster, then come back to compare.',
        testId: 'comparison-rosters-unavailable',
    },
    error: {
        title: 'Couldn’t load rosters',
        body: 'The roster list didn’t load, so there is nothing to compare. Use Try again in Filters.',
        testId: 'comparison-rosters-failed',
    },
};

const RosterComparisonTable: React.FC = observer(() => {
    const networkStore = useContext(NetworkContext);
    const comparisonStore = networkStore.rosterComparisonStore;
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<ParsedApiError | null>(null);
    // In the store rather than local state so the summary panel reads the same measure
    const activeMetric = comparisonStore.measure;
    const [showPercentages, setShowPercentages] = useState(() => DEFAULT_PERCENTAGES[comparisonStore.measure]);
    const setActiveMetric = (metric: ComparisonMeasure) => {
        // Re-selecting the current view is no change of view, so it keeps the user's choice
        if (metric === activeMetric) return;
        comparisonStore.setMeasure(metric);
        setShowPercentages(DEFAULT_PERCENTAGES[metric]);
    };
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(PAGE_SIZE);
    // In the store too, so the summary panel describes the same specialties
    const selectedSpecialties = comparisonStore.selectedSpecialties;
    const setSelectedSpecialties = (names: string[]) => comparisonStore.setSelectedSpecialties(names);
    const metricButtonRefs = useRef<Array<HTMLButtonElement | null>>([]);

    const handleMetricKeyDown = (event: React.KeyboardEvent, index: number) => {
        const offset = METRIC_KEY_OFFSETS[event.key];
        if (offset === undefined) return;
        event.preventDefault();
        const next = (index + offset + METRIC_OPTIONS.length) % METRIC_OPTIONS.length;
        metricButtonRefs.current[next]?.focus();
        setActiveMetric(METRIC_OPTIONS[next].key);
    };

    // Loads the roster list if nothing has, so the table never waits on the filter panel to fetch it
    const {status: rosterListStatus} = useProviderRosterList();
    const rosterListPending = rosterListStatus === 'idle' || rosterListStatus === 'loading';
    const rosterListMessage = rosterListStatus === 'empty' || rosterListStatus === 'error'
        ? ROSTER_LIST_MESSAGES[rosterListStatus]
        : null;

    // The selected rosters the loaded rows have figures for, each with the number its response
    // fields carry, numbered as the summary panel numbers them. In slot order, so changing the
    // primary never moves a column
    const {responseRosters} = comparisonStore;
    const selectedRosterIds = useMemo(
        () => [...responseRosters].sort((a, b) => a.slot - b.slot),
        [responseRosters],
    );

    useEffect(() => {
        const controller = new AbortController();
        let isActive = true;
        // New data can have fewer pages than the current one, which would leave an empty table
        setPage(0);
        const loadData = async () => {
            setError(null);
            try {
                if (isActive) setLoading(true);
                if (networkStore.runId) {
                    await comparisonStore.loadNetworkAdequacyComparison(networkStore.runId, controller);
                }
            } catch (err) {
                if (isAbortError(err as Error)) return;
                setError(handleError(err as Error, null, 'Failed to load network adequacy comparison'));
            } finally {
                if (isActive) setLoading(false);
            }
        };
        // Until the roster list is ready the table shows its state instead, which would hide the result
        if (comparisonStore.isReady && rosterListStatus === 'ready') {
            loadData();
        } else {
            // The last load was for a selection that no longer applies, so its outcome is dropped
            setError(null);
            setLoading(false);
        }
        return () => {
            isActive = false;
            controller.abort();
        };
    }, [
        comparisonStore.comparisonFilters.standard_type,
        comparisonStore.comparisonFilters.standard_version,
        comparisonStore.comparisonFilters.patient_roster_id,
        comparisonStore.comparisonFilters.state,
        comparisonStore.comparisonFilters.county,
        comparisonStore.comparisonFilters.zip,
        comparisonStore.rosterSelectionKey,
        // The request payload sends county or zip depending on grain, so a grain switch must reload
        comparisonStore.geographyGrain,
        networkStore.runId,
        comparisonStore.isReady,
        rosterListStatus
    ]);

    const getRosterMeta = useCallback(
        (rosterId: string, revisionId: string) => {
            const roster = networkStore.providerRosters?.find(
                (r) => r.rosterId === rosterId && r.revisionId === revisionId,
            );
            return {
                shortName: roster?.rosterName || rosterId,
                revision: roster && roster.revisionId !== '0' ? roster.revisionName : undefined,
            };
        },
        [networkStore.providerRosters],
    );

    useEffect(() => {
        setPage(0);
    }, [selectedSpecialties, comparisonStore.comparisonData, pageSize]);

    const showComparison = comparisonStore.isReady;
    const standards = MEASURE_STANDARDS[activeMetric];
    const figureCount = showPercentages ? standards.length : 0;

    const countyDisplay = parseCountyValue(comparisonStore.comparisonFilters.county).countyName;
    const stateDisplay = comparisonStore.comparisonFilters.state || '';
    const locationLabel = [countyDisplay, stateDisplay].filter(Boolean).join(', ');
    const standardLabel = [
        comparisonStore.comparisonFilters.standard_type,
        comparisonStore.comparisonFilters.standard_version,
    ].filter(Boolean).join(' ');
    const geographyNoun = comparisonStore.geographyGrain === 'ZIP' ? 'ZIP code' : 'county';
    const noDataTitle = `No data for ${locationLabel || `this ${geographyNoun}`}`;

    const availableSpecialties = useMemo(() => {
        const raw = comparisonStore.comparisonData || [];
        const unique = Array.from(new Set(raw.map((item: any) => item.specialtyName).filter(Boolean)));
        return (unique as string[]).sort((a, b) => a.localeCompare(b));
    }, [comparisonStore.comparisonData]);

    // Reloaded data can lack specialties picked for the previous data, which would filter out every
    // row. The data is cleared while a reload is in flight, so only loaded data drops a selection.
    useEffect(() => {
        if (!comparisonStore.comparisonData) return;
        const kept = selectedSpecialties.filter(s => availableSpecialties.includes(s));
        if (kept.length !== selectedSpecialties.length) {
            comparisonStore.setSelectedSpecialties(kept);
        }
    }, [comparisonStore, comparisonStore.comparisonData, availableSpecialties, selectedSpecialties]);

    // Narrowed by the store, which the summary panel reads the same rows from
    const filteredData = useMemo(
        () => comparisonStore.filteredComparisonData || [],
        [comparisonStore.filteredComparisonData],
    );

    // Field numbers of the rosters with data anywhere in the geography. Read over every row, not the
    // Specialty filter's, as the summary panel reads them, so the two agree on what it leaves out
    const rostersWithData = useMemo(() => {
        const all = comparisonStore.comparisonData || [];
        return new Set(selectedRosterIds.filter((r) => rosterHasData(all, r.fieldNumber)).map((r) => r.fieldNumber));
    }, [comparisonStore.comparisonData, selectedRosterIds]);
    // A grid of dashes would say nothing, so an empty pattern stands in; the summary carries the error
    const noRosterHasData = selectedRosterIds.length > 0
        && (comparisonStore.comparisonData?.length ?? 0) > 0
        && rostersWithData.size === 0;

    const headers = useMemo(() => {
        const rosterHeaders = selectedRosterIds.map((r) => {
            const meta = getRosterMeta(r.rosterId, r.revisionId);
            return {
                key: `${r.rosterId}-${r.revisionId}`,
                header: meta.shortName,
                _slot: r.slot,
                _revision: meta.revision,
            };
        });
        return [
            {key: 'specialty', header: 'Specialty'},
            ...rosterHeaders,
            {key: 'complianceTarget', header: 'Compliance Standard'},
        ];
    }, [selectedRosterIds, getRosterMeta]);

    const pageData = useMemo(
        () => (showComparison ? filteredData.slice(page * pageSize, (page + 1) * pageSize) : []),
        [showComparison, filteredData, page, pageSize],
    );

    const rows = useMemo(() => {
        return (
            pageData.map((item: any, idx: number) => {
                const rosterColumns = selectedRosterIds.reduce(
                    (acc, r) => {
                        // Per-roster fields are numbered by the roster's position in the request
                        const n = r.fieldNumber;
                        // No data is not a miss: a dash in the tick's place, in secondary text
                        if (!hasRosterData(item, n)) {
                            acc[`${r.rosterId}-${r.revisionId}`] = (
                                <span className='comparison-no-data' role='img' aria-label={`Roster ${r.slot}: no data`}>—</span>
                            );
                            return acc;
                        }
                        const meets = meetsMeasure(item, activeMetric, n);
                        const figures = showPercentages
                            ? standards.map((standard) => ({standard, text: formatPercent(item[populationMetKey(standard, n)])}))
                            : [];
                        // As the prototype names it: the verdict and measure the legend gives a sighted
                        // reader, and each figure with the label the header gives it
                        const ariaLabel = [
                            `Roster ${r.slot}: ${meets ? 'meets' : 'does not meet'} ${activeMetric} standard`,
                            figures.map(({standard, text}) => `${STANDARD_LABELS[standard]} ${text}`).join(', '),
                        ].filter(Boolean).join(' — ');
                        const icon = <ComplianceIcon isCompliant={meets} ariaLabel={ariaLabel} />;

                        acc[`${r.rosterId}-${r.revisionId}`] = figures.length ? (
                            <span className='comparison-result comparison-pct text-xs'>
                                {icon}
                                {/* Hidden from screen readers, which hear them in the tick's name */}
                                {figures.map(({standard, text}) => <span key={standard} aria-hidden='true'>{text}</span>)}
                            </span>
                        ) : icon;
                        return acc;
                    },
                    {} as Record<string, JSX.Element>,
                );

                let complianceTargetDisplay: string;
                if (activeMetric === 'time') {
                    complianceTargetDisplay = formatPercent(item.timeComplianceTarget);
                } else if (activeMetric === 'distance') {
                    complianceTargetDisplay = formatPercent(item.distanceComplianceTarget);
                } else {
                    const timePct = formatPercent(item.timeComplianceTarget);
                    const distPct = formatPercent(item.distanceComplianceTarget);
                    complianceTargetDisplay = timePct === distPct
                        ? timePct
                        : `Time ${timePct} / Dist ${distPct}`;
                }

                // A specialty a roster with data lacks is left out of the summary for every roster.
                // A roster with no data at all is excluded from it as a whole, so marks no row
                const leftOut = selectedRosterIds.some(
                    (r) => rostersWithData.has(r.fieldNumber) && !hasRosterData(item, r.fieldNumber),
                );

                return {
                    id: `${idx}`,
                    specialty: leftOut ? (
                        <>
                            {item.specialtyName}
                            <span className='comparison-left-out'>Not in summary</span>
                        </>
                    ) : item.specialtyName,
                    complianceTarget: complianceTargetDisplay,
                    ...rosterColumns,
                };
            })
        );
    }, [selectedRosterIds, pageData, rostersWithData, activeMetric, standards, showPercentages]);

    // Get requirements gate status for state machine
    // Note: Don't use useMemo here - let MobX reactivity handle it since we're in an observer component
    const requirementsStatus = comparisonStore.getBlockingRequirement();

    // The store only knows the primary roster's IDs; name it the way the roster dropdowns do.
    // Until provider rosters load, the store's ID-based message stands in.
    const primaryRoster = comparisonStore.primaryRoster;
    const primaryRosterDetails = primaryRoster
        ? networkStore.providerRosters.find(
            r => r.rosterId === primaryRoster.rosterId && r.revisionId === primaryRoster.revisionId,
        )
        : undefined;
    const requirements = requirementsStatus.requirements.map(req => (
        req.id === 'primary-roster' && req.met && primaryRosterDetails
            ? {...req, message: formatRosterLabel(primaryRosterDetails)}
            : req
    ));

    const isGated = !requirementsStatus.isReady;
    const completedRequirements = requirementsStatus.requirements.filter(r => r.met).length;
    const totalRequirements = requirementsStatus.requirements.length;

    // Determine which empty state to show based on two-tier state machine.
    // Roster-list states (pending, empty, failed) are handled before this, as no selection can be made without them.
    const getEmptyStateVariant = (): 'requirements-checklist' | 'error' | null => {
        // Requirements gate (checklist) first: an error is from the last load, which needed a complete selection
        if (isGated) return 'requirements-checklist';

        // System states (error only - loading shows skeleton)
        if (error) return 'error';

        return null; // Show table or skeleton
    };

    const emptyStateVariant = getEmptyStateVariant();
    const showTableSkeleton = !emptyStateVariant && loading && !comparisonStore.comparisonData;

    // Screen reader announcements for state changes, in the same order as the render below
    const getAnnouncementMessage = (): string => {
        if (rosterListPending) return '';
        if (rosterListMessage) return rosterListMessage.title;
        if (emptyStateVariant === 'requirements-checklist') {
            return `Requirements checklist: ${completedRequirements} of ${totalRequirements} requirements complete`;
        }
        if (emptyStateVariant === 'error') return `Error loading comparison data: ${error?.message}`;
        if (showTableSkeleton) return 'Loading roster comparison data';
        if (!emptyStateVariant && noRosterHasData) return noDataTitle;
        if (!emptyStateVariant && comparisonStore.comparisonData) {
            return 'Roster comparison data loaded successfully';
        }
        return '';
    };

    // The only live region for this view: ComparisonEmptyState renders none of its own
    useScreenReaderAnnouncement(getAnnouncementMessage(), emptyStateVariant === 'error' ? 'assertive' : 'polite');

    return (
        <div className='flex flex-1 relative min-w-0 overflow-hidden' id='roster-comparison-table' style={{background: '#f4f4f4'}}>
            <div className='flex-1 p-6 min-w-0'>
                <div className='grid grid-cols-1 gap-6'>
                    {/* Comparison Table */}
                    <div className='col-span-1 min-w-0'>
                        {rosterListPending ? (
                            // The skeleton is the loading state; a "Loading rosters" message beside it would repeat it
                            <div data-testid='comparison-rosters-pending'>
                                <ComparisonTableSkeleton headers={headers} />
                            </div>
                        ) : rosterListMessage ? (
                            <ComparisonMessage {...rosterListMessage} />
                        ) : emptyStateVariant ? (
                            <ComparisonEmptyState
                                variant={emptyStateVariant}
                                requirements={requirements}
                                errorMessage={error?.message}
                                completedCount={completedRequirements}
                                totalCount={totalRequirements}
                            />
                        ) : showTableSkeleton ? (
                            <ComparisonTableSkeleton headers={headers} />
                        ) : (
                            <>
                                {/* Toggle bar — MPDS content switcher + Show percentages */}
                                <div className='flex items-center flex-wrap gap-4 mb-4' style={{minHeight: '2rem'}} data-testid='metric-toggle-bar'>
                                    <div className='mpds-switcher' role='radiogroup' aria-label='Measure'>
                                        {METRIC_OPTIONS.map((option, index) => (
                                            <button
                                                key={option.key}
                                                ref={(el) => {
                                                    metricButtonRefs.current[index] = el;
                                                }}
                                                className='mpds-switcher__btn'
                                                role='radio'
                                                type='button'
                                                aria-checked={activeMetric === option.key}
                                                tabIndex={activeMetric === option.key ? 0 : -1}
                                                onClick={() => setActiveMetric(option.key)}
                                                onKeyDown={(event) => handleMetricKeyDown(event, index)}
                                            >
                                                <svg viewBox='0 0 16 16' aria-hidden='true' focusable='false'>
                                                    {option.icon}
                                                </svg>
                                                <span data-text={option.label}>{option.label}</span>
                                            </button>
                                        ))}
                                    </div>
                                    <label className='mpds-toggle'>
                                        <span className='mpds-label-01'>Show percentages</span>
                                        <input
                                            type='checkbox'
                                            role='switch'
                                            id='show-percentages-toggle'
                                            checked={showPercentages}
                                            onChange={(e) => setShowPercentages(e.target.checked)}
                                            className='mpds-toggle__input'
                                        />
                                        <span className='mpds-toggle__track' aria-hidden='true' />
                                    </label>
                                </div>

                                {/* Card — county header + table + footer */}
                                {/* No overflow: hidden here: the specialty menu opens downward inside this card and would be clipped */}
                                <div className='bg-white' style={{border: '1px solid #e0e0e0', borderRadius: '4px'}}>
                                    <div style={{padding: '1rem 1rem 1.5rem'}} data-testid='comparison-header'>
                                        <div className='comparison-header__row'>
                                            <div className='comparison-header__text'>
                                                {locationLabel && (
                                                    <h2 style={{margin: 0, fontSize: '1.25rem', lineHeight: '1.75rem', fontWeight: 400, color: '#161616'}}>{locationLabel}</h2>
                                                )}
                                                <p style={{margin: 0, fontSize: '0.875rem', lineHeight: '1.125rem', letterSpacing: '0.16px', color: '#525252'}}>
                                                    Network adequacy by specialty across {selectedRosterIds.length} roster{selectedRosterIds.length !== 1 ? 's' : ''}
                                                    {standardLabel ? `, measured against ${standardLabel}.` : '.'}
                                                </p>
                                                <div style={{display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '1rem', marginTop: '0.5rem', fontSize: '0.75rem', lineHeight: '1.33333', letterSpacing: '0.32px', color: '#6f6f6f'}}>
                                                    <span className='inline-flex items-center gap-1.5'>
                                                        <CheckmarkFilled className='comparison-icon comparison-icon--pass' size={16} />
                                                        {MEETS_LABELS[activeMetric]}
                                                    </span>
                                                    <span className='inline-flex items-center gap-1.5'>
                                                        <CloseFilled className='comparison-icon comparison-icon--fail' size={16} />
                                                        Below the standard
                                                    </span>
                                                </div>
                                            </div>
                                            <div className='comparison-header__filter'>
                                                <SpecialtyMultiSelect
                                                    specialties={availableSpecialties}
                                                    selectedSpecialties={selectedSpecialties}
                                                    onChange={setSelectedSpecialties}
                                                    showChips={false}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div
                                        className='comparison-table-card'
                                        aria-busy={loading}
                                        style={{opacity: loading ? 0.5 : 1, transition: 'opacity 150ms', pointerEvents: loading ? 'none' : undefined}}
                                    >
                                        {/* Inside the card, so the header and its Specialty select stay put */}
                                        {noRosterHasData ? (
                                            <div className='comparison-no-data-state' data-testid='comparison-no-data'>
                                                <h5 className='comparison-no-data-state__title'>{noDataTitle}</h5>
                                                <p className='comparison-no-data-state__body'>
                                                    None of the selected rosters have data for this {geographyNoun}.
                                                </p>
                                            </div>
                                        ) : (
                                            <>
                                                {/* Focusable so keyboard users can scroll a table wider than the viewport */}
                                                <div
                                                    className='comparison-table-scroll'
                                                    style={{maxHeight: '36rem', overflow: 'auto'}}
                                                    data-testid='comparison-table-container'
                                                    role='region'
                                                    tabIndex={0}
                                                    aria-label='Roster compliance comparison table, scrollable'
                                                >
                                                    <DataTable rows={rows} headers={headers} size='lg'>
                                                        {({rows, headers: dtHeaders, getHeaderProps, getRowProps, getTableProps}) => {
                                                            // The stylesheet sizes the table's minimum width from these
                                                            const widthVars = {
                                                                '--roster-count': selectedRosterIds.length,
                                                                '--figure-count': figureCount,
                                                            } as React.CSSProperties;

                                                            return (
                                                                <Table
                                                                    {...getTableProps()}
                                                                    aria-label='Roster compliance comparison'
                                                                    className='comparison-table'
                                                                    style={{tableLayout: 'fixed', width: '100%', ...widthVars}}
                                                                >
                                                                    <TableHead>
                                                                        <TableRow>
                                                                            {(dtHeaders as any[]).map((header) => {
                                                                                const isSpecialty = header.key === 'specialty';
                                                                                const isCompliance = header.key === 'complianceTarget';
                                                                                const colClass = isSpecialty ? 'no-spech' : isCompliance ? 'no-stdh' : 'no-rosterh';
                                                                                return (
                                                                                    <TableHeader
                                                                                        {...getHeaderProps({header})}
                                                                                        key={header.key}
                                                                                        className={colClass}
                                                                                        style={{verticalAlign: header._slot ? 'top' : 'middle'}}
                                                                                    >
                                                                                        {header._slot ? (
                                                                                            <div>
                                                                                                <div className='flex items-center gap-1.5 min-w-0'>
                                                                                                    <RosterBadge slot={header._slot} />
                                                                                                    <span className='font-semibold text-sm truncate min-w-0' title={header.header}>{header.header}</span>
                                                                                                </div>
                                                                                                {header._revision && (
                                                                                                    <div className='comparison-revision text-xs font-normal mt-0.5 pl-6 truncate'>
                                                                                                rev. &mdash; {header._revision}
                                                                                                    </div>
                                                                                                )}
                                                                                                {/* Labelled only with two figures to tell apart; with one, the switcher names it */}
                                                                                                {figureCount > 1 && (
                                                                                                    <div className='comparison-subhead'>
                                                                                                        <span />
                                                                                                        {standards.map((standard) => (
                                                                                                            <span key={standard}>{STANDARD_LABELS[standard]}</span>
                                                                                                        ))}
                                                                                                    </div>
                                                                                                )}
                                                                                            </div>
                                                                                        ) : (
                                                                                            header.header
                                                                                        )}
                                                                                    </TableHeader>
                                                                                );
                                                                            })}
                                                                        </TableRow>
                                                                    </TableHead>
                                                                    <TableBody>
                                                                        {rows.length === 0 ? (
                                                                            <TableRow>
                                                                                <TableCell
                                                                                    colSpan={dtHeaders.length}
                                                                                    className='p-3'
                                                                                    style={{textAlign: 'center', fontSize: '1rem', fontWeight: '500'}}
                                                                                >
                                                                            No Data Available
                                                                                </TableCell>
                                                                            </TableRow>
                                                                        ) : (
                                                                            rows.map((row) => (
                                                                                <TableRow
                                                                                    {...getRowProps({row})}
                                                                                    key={row.id}
                                                                                    className='comparison-row'
                                                                                >
                                                                                    {row.cells.map((cell, cellIndex) => {
                                                                                        const hKey = headers[cellIndex]?.key;
                                                                                        const isSpecialty = hKey === 'specialty';
                                                                                        if (isSpecialty) {
                                                                                            // Row header, so a screen reader announces the specialty with every result cell
                                                                                            return (
                                                                                                <th
                                                                                                    key={cell.id}
                                                                                                    scope='row'
                                                                                                    className='no-spec-cell'
                                                                                                    // The name alone, without a "Not in summary" beneath it
                                                                                                    title={pageData[Number(row.id)]?.specialtyName}
                                                                                                >
                                                                                                    {cell.value}
                                                                                                </th>
                                                                                            );
                                                                                        }
                                                                                        return (
                                                                                            <TableCell key={cell.id}>
                                                                                                {cell.value}
                                                                                            </TableCell>
                                                                                        );
                                                                                    })}
                                                                                </TableRow>
                                                                            ))
                                                                        )}
                                                                    </TableBody>
                                                                </Table>
                                                            );
                                                        }}
                                                    </DataTable>
                                                </div>
                                                {!loading && !error && rows.length > 0 && (
                                                    <Pagination
                                                        totalItems={filteredData.length}
                                                        pageSize={pageSize}
                                                        pageSizes={[15, 20, 50, 100]}
                                                        page={page + 1}
                                                        onChange={({page: p, pageSize: ps}) => {
                                                            if (ps !== pageSize) {
                                                                setPageSize(ps);
                                                                setPage(0);
                                                            } else {
                                                                setPage(p - 1);
                                                            }
                                                        }}
                                                    />
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
});

export default RosterComparisonTable;
